
import React, { useState, useEffect } from "react";
import TeacherLayout from "@/components/TeacherLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Users, BookOpen, Clock, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { createApiUrl } from "@/lib/api";

interface Class {
  _id: string;
  name: string;
  subject: string;
  grade: string;
  students_count: number;  
  created_at: string;
  status?: string;
}

interface ClassesResponse {
  classes: Class[];
}

const AttendanceOverview = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      setError(null);
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/teacher/login');
        return;
      }

      console.log('Fetching classes for attendance...');
      const response = await fetch(createApiUrl('/teacher/classes'), {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      console.log('Classes API response status:', response.status);

      if (response.ok) {
        const data: ClassesResponse = await response.json();
        console.log('Classes API response data:', data);
        
        const classesArray = Array.isArray(data) ? data : (data.classes || []);
        const availableClasses = classesArray.filter(cls => cls && cls._id);
        
        console.log('Available classes for attendance:', availableClasses);
        setClasses(availableClasses);
        
        if (availableClasses.length === 0) {
          setError('No classes found. Create a class first to mark attendance.');
        }
      } else if (response.status === 401) {
        localStorage.removeItem('teacherData');
        localStorage.removeItem('accessToken');
        toast({
          title: "Session Expired",
          description: "Please login again",
          variant: "destructive"
        });
        navigate('/teacher/login');
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch classes:', response.status, errorText);
        setError(`Failed to load classes: ${response.status}`);
      }
    } catch (error) {
      console.error('Classes fetch error:', error);
      setError('Network error: Failed to load classes');
      toast({
        title: "Error",
        description: "Failed to load classes",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAttendance = (classId: string, className: string) => {
    console.log('Navigating to attendance for class:', classId, className);
    navigate(`/teacher/attendance/${classId}`, { 
      state: { className } 
    });
  };

  if (isLoading) {
    return (
      <TeacherLayout>
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm sm:text-base">Loading your classes...</p>
          </div>
        </div>
      </TeacherLayout>
    );
  }

  const todaysDate = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg p-4 sm:p-6 text-white">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Calendar className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl font-bold">Attendance Management</h1>
          </div>
          <p className="text-indigo-100 text-sm sm:text-base">
            Mark attendance for {todaysDate}
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{classes.length}</div>
              <p className="text-xs sm:text-sm text-gray-600">Total Classes</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-green-600">
                {classes.reduce((total, cls) => total + (cls.students_count || 0), 0)}
              </div>
              <p className="text-xs sm:text-sm text-gray-600">Total Students</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-3 sm:p-4 text-center">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-600 mx-auto mb-2" />
              <div className="text-xl sm:text-2xl font-bold text-orange-600">
                {new Date().toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </div>
              <p className="text-xs sm:text-sm text-gray-600">Current Time</p>
            </CardContent>
          </Card>
        </div>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 text-red-600 text-sm sm:text-base">
                <span className="font-medium">Error:</span>
                <span className="break-words">{error}</span>
              </div>
              <Button 
                onClick={fetchClasses}
                variant="outline"
                size="sm"
                className="mt-2 text-xs sm:text-sm"
              >
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Classes List */}
        <div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
            Select Class for Attendance
          </h2>
          
          {classes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {classes.map((classItem) => (
                <Card key={classItem._id} className="hover:shadow-lg transition-all duration-200 border-2 hover:border-indigo-200">
                  <CardHeader className="pb-2 sm:pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className="p-1.5 sm:p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                          <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <CardTitle className="text-base sm:text-lg font-semibold text-gray-800 truncate">
                            {classItem.name}
                          </CardTitle>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">{classItem.subject}</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="space-y-2 sm:space-y-3">
                      {/* Class Details */}
                      <div className="flex items-center justify-between text-xs sm:text-sm">
                        <div className="flex items-center gap-1 sm:gap-2 min-w-0 flex-1">
                          <Users className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                          <span className="text-gray-600 truncate">
                            {classItem.students_count || 0} students
                          </span>
                        </div>
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium flex-shrink-0">
                          Grade {classItem.grade}
                        </span>
                      </div>
                      
                      {/* Status Badge */}
                      {classItem.status && (
                        <div className="flex justify-start">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            classItem.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {classItem.status}
                          </span>
                        </div>
                      )}
                      
                      {/* Action Button */}
                      <Button 
                        onClick={() => handleMarkAttendance(classItem._id, classItem.name)}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-xs sm:text-sm"
                        size="sm"
                      >
                        <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                        Mark Attendance
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : !isLoading && !error && (
            <Card className="text-center py-8 sm:py-12">
              <CardContent>
                <div className="flex flex-col items-center space-y-3 sm:space-y-4">
                  <div className="p-3 sm:p-4 bg-gray-100 rounded-full">
                    <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">
                      No Classes Available
                    </h3>
                    <p className="text-gray-500 mb-3 sm:mb-4 text-sm sm:text-base px-4">
                      You need to create classes before marking attendance.
                    </p>
                    <Button 
                      onClick={() => navigate('/teacher/classes')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-sm"
                      size="sm"
                    >
                      Create Your First Class
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        {classes.length > 0 && (
          <Card>
            <CardHeader className="pb-3 sm:pb-4">
              <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/teacher/classes')}
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  size="sm"
                >
                  <BookOpen className="w-3 h-3 sm:w-4 sm:h-4" />
                  Manage Classes
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/teacher/students')}
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  size="sm"
                >
                  <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                  View Students
                </Button>
                <Button 
                  variant="outline" 
                  onClick={fetchClasses}
                  className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
                  size="sm"
                >
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                  Refresh Classes
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </TeacherLayout>
  );
};

export default AttendanceOverview;
